import type { ComponentType } from 'react';

/**
 * Represents a single breadcrumb item
 */
export interface BreadcrumbItem {
  /**
   * The text label for this breadcrumb
   */
  label: string;
  /**
   * The href for this breadcrumb
   * If undefined, breadcrumb is not clickable (typically the current page)
   */
  href?: string;
  /**
   * Optional icon component to display with the breadcrumb
   */
  icon?: ComponentType;
  /**
   * Whether the breadcrumb is disabled
   * @default false
   */
  isDisabled?: boolean;
  /**
   * Optional search params to preserve when navigating to this breadcrumb
   * These will be appended to the href as query parameters
   */
  search?: Record<string, unknown>;
  /**
   * Breadcrumb trail carried as navigation state when this crumb is clicked,
   * so the destination page keeps the context above it (e.g. the product
   * crumb). Keep entries serializable; render-only fields are stripped by
   * builders.
   */
  trail?: BreadcrumbItem[];
  /**
   * Optional image source for the breadcrumb
   * When present, the breadcrumb renders a 32x32 image thumbnail instead of
   * the text label, and `label` becomes the accessible name (aria-label/alt)
   */
  thumbnailSrc?: string;
}

/**
 * Navigation breadcrumb trail stored in location.state
 * Contains the breadcrumb trail leading to the current page
 */
export interface NavigationFromState {
  /**
   * Array of breadcrumbs representing the navigation trail
   * Each item should be clickable (have href)
   */
  trail: BreadcrumbItem[];
}

/**
 * Builds dynamic breadcrumbs based on navigation state
 *
 * Use this utility in route loaders when you need to show breadcrumbs
 * but don't know the exact navigation path (e.g., user could arrive from
 * multiple different pages).
 *
 * This function preserves the breadcrumb trail across multiple navigation levels.
 * For example: Home → Library → PDP → Asset will maintain the full trail.
 *
 * @param from - Navigation metadata from location.state, or undefined if direct navigation
 * @param currentPageLabel - Label for the current page
 * @param currentPageHref - Optional href for the current page (used when preserving state for child pages)
 * @param currentPageSearch - Optional search params for the current page (preserved in breadcrumb trail)
 * @param fallbackBreadcrumb - Breadcrumb to show if no navigation state exists (defaults to Library)
 * @returns Array of breadcrumb items including the full trail and current page
 *
 * @example
 * // In route loader (leaf page - no href for current page)
 * loader: ({ location }) => ({
 *   breadcrumbs: buildDynamicBreadcrumbs(
 *     location.state?.from,
 *     'Product Details'
 *   )
 * })
 *
 * @example
 * // In route loader (preserving search params for navigation back)
 * loader: ({ location, params }) => ({
 *   breadcrumbs: buildDynamicBreadcrumbs(
 *     location.state?.from,
 *     'Product Details',
 *     `/library/products/${params.pdpId}`,
 *     location.search
 *   )
 * })
 *
 * @example
 * // When navigating with state (use useBreadcrumbs to get current trail)
 * const currentBreadcrumbs = useBreadcrumbs();
 * navigateWithState(router, {
 *   to: '/library/assets/$id',
 *   params: { id },
 *   from: { trail: currentBreadcrumbs.slice(0, -1) } // Exclude current page
 * })
 */
export function buildDynamicBreadcrumbs(
  from: NavigationFromState | undefined,
  currentPageLabel: string,
  currentPageHref?: string,
  currentPageSearch?: Record<string, unknown>,
  fallbackBreadcrumb: BreadcrumbItem = { label: 'Library', href: '/library' },
): BreadcrumbItem[] {
  const breadcrumbs: BreadcrumbItem[] = [];

  // Add the breadcrumb trail or fallback
  if (from?.trail && from.trail.length > 0) {
    breadcrumbs.push(...from.trail);
  } else {
    breadcrumbs.push(fallbackBreadcrumb);
  }

  // Add current page as the last breadcrumb
  // If currentPageHref is provided, include it with search params (for child pages to navigate back)
  // Otherwise, no href = not clickable (current active page)
  breadcrumbs.push({
    label: currentPageLabel,
    href: currentPageHref,
    search: currentPageSearch,
  });

  return breadcrumbs;
}
